import React, { useEffect, useMemo, useState } from "react";
import {
  Sparkles,
  Wand2,
  PenLine,
  Image as ImageIcon,
  Layers,
  CheckCircle2,
  Loader2,
} from "lucide-react";

/**
 * Polished AI loading state used across Generate / Regenerate / Generate-image
 * / Autopilot Generate-week. Replaces the plain "Generating week..." string
 * with reassuring rotating status copy + an optional step indicator.
 *
 * Variants:
 *   - "generate"   : single post generation (default)
 *   - "image"      : AI image generation
 *   - "regenerate" : regenerate caption/full post
 *   - "week"       : Autopilot weekly batch (shows the 5-step indicator)
 *
 * Props:
 *   variant     - one of the above
 *   compact     - true to render the inline pill (no card)
 *   testidPrefix
 *   slowAfterMs - when the elapsed time crosses this threshold, append a
 *                 reassuring "Still working..." line. Defaults to 12s.
 */

const MESSAGES = {
  generate: [
    { icon: PenLine, text: "Understanding your business…" },
    { icon: Sparkles, text: "Writing captions in your brand voice…" },
    { icon: Wand2, text: "Choosing the best posting angle…" },
    { icon: Layers, text: "Polishing hashtags + call-to-action…" },
    { icon: CheckCircle2, text: "Almost done — preparing your draft…" },
  ],
  image: [
    { icon: ImageIcon, text: "Composing a scroll-stopping shot…" },
    { icon: Wand2, text: "Setting the lighting and mood…" },
    { icon: Layers, text: "Refining colors and detail…" },
    { icon: Sparkles, text: "Almost there — adding the finishing touches…" },
  ],
  regenerate: [
    { icon: PenLine, text: "Re-thinking the angle…" },
    { icon: Sparkles, text: "Tightening the hook…" },
    { icon: Wand2, text: "Choosing a fresh CTA…" },
    { icon: CheckCircle2, text: "Saving your new draft…" },
  ],
  week: [
    { icon: Layers, text: "Reviewing your business profile…" },
    { icon: Sparkles, text: "Planning the week…" },
    { icon: PenLine, text: "Writing captions…" },
    { icon: ImageIcon, text: "Choosing images…" },
    { icon: CheckCircle2, text: "Scheduling drafts…" },
  ],
};

const STEP_LABELS = {
  week: [
    "Reviewing your business profile",
    "Planning the week",
    "Writing captions",
    "Choosing images",
    "Scheduling drafts",
  ],
};

export default function AILoadingState({
  variant = "generate",
  compact = false,
  testidPrefix = "ai-loading",
  slowAfterMs = 12000,
}) {
  const messages = MESSAGES[variant] || MESSAGES.generate;
  const stepLabels = STEP_LABELS[variant] || null;
  const [tick, setTick] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const messageTimer = setInterval(() => setTick((t) => t + 1), 2200);
    const elapsedTimer = setInterval(() => setElapsed(Date.now() - start), 1000);
    return () => {
      clearInterval(messageTimer);
      clearInterval(elapsedTimer);
    };
  }, []);

  const current = messages[tick % messages.length];
  const CurrentIcon = current.icon;
  const slow = elapsed >= slowAfterMs;

  // Step progress mirrors the rotating message index but always increases
  // monotonically up to the last step (so the UI doesn't appear to "go
  // backwards" once we loop). Capped at messages.length - 1.
  const stepIndex = useMemo(() => Math.min(tick, messages.length - 1), [tick, messages.length]);

  if (compact) {
    return (
      <div
        className="inline-flex items-center gap-2 text-xs text-[color:var(--text-2)]"
        data-testid={`${testidPrefix}-compact`}
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--brand-primary)]" />
        <span className="truncate">{current.text}</span>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5"
      data-testid={testidPrefix}
    >
      <div className="flex items-start gap-3">
        <div className="relative h-10 w-10 rounded-lg bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] flex items-center justify-center text-[color:var(--bg-0)] shadow-[0_4px_12px_rgba(27,168,159,0.35)] shrink-0">
          <CurrentIcon className="h-5 w-5 animate-pulse" />
          {/* Subtle halo */}
          <span className="absolute inset-0 rounded-lg ring-1 ring-[color:rgba(27,168,159,0.30)] animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="font-display text-base font-semibold text-[color:var(--text-1)] transition-opacity"
            data-testid={`${testidPrefix}-message`}
            key={tick}
          >
            {current.text}
          </div>
          <div className="text-[12px] text-[color:var(--text-3)] mt-1">
            {variant === "week"
              ? "Crafting a week of on-brand content. This usually takes a moment."
              : "Hang tight — high-quality output takes a few seconds."}
          </div>

          {/* Indeterminate progress bar */}
          <div
            className="mt-3 h-1.5 rounded-full bg-[color:var(--surface-2)] overflow-hidden"
            aria-hidden="true"
          >
            <div
              className="h-full w-1/3 rounded-full bg-gradient-to-r from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)]"
              style={{
                animation: "aiLoadingSweep 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite",
              }}
            />
          </div>

          {slow ? (
            <div
              className="mt-2 text-[12px] text-[color:var(--accent-warm)]"
              data-testid={`${testidPrefix}-slow-note`}
            >
              Still working — larger content batches can take a little longer.
            </div>
          ) : null}
        </div>
      </div>

      {/* Step indicator (Autopilot week only) */}
      {stepLabels ? (
        <ol
          className="mt-4 grid gap-1.5 text-[12px]"
          data-testid={`${testidPrefix}-steps`}
        >
          {stepLabels.map((label, i) => {
            const done = i < stepIndex;
            const active = i === stepIndex;
            return (
              <li
                key={label}
                className={
                  "flex items-center gap-2 " +
                  (active
                    ? "text-[color:var(--text-1)] font-medium"
                    : done
                    ? "text-[color:var(--text-2)]"
                    : "text-[color:var(--text-3)]")
                }
                data-testid={`${testidPrefix}-step-${i}`}
              >
                <span
                  className={
                    "h-4 w-4 rounded-full flex items-center justify-center text-[10px] shrink-0 " +
                    (done
                      ? "bg-[color:var(--success)] text-white"
                      : active
                      ? "border border-[color:var(--brand-primary)] text-[color:var(--brand-primary)]"
                      : "border border-[color:var(--border-subtle)] text-[color:var(--text-3)]")
                  }
                >
                  {done ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                </span>
                <span className="truncate">{label}</span>
                {active ? (
                  <Loader2 className="h-3 w-3 animate-spin text-[color:var(--brand-primary)] ml-auto shrink-0" />
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : null}

      {/* Keyframes scoped via a <style> tag so we don't need a Tailwind config change */}
      <style>{`
        @keyframes aiLoadingSweep {
          0% { transform: translateX(-110%); }
          100% { transform: translateX(310%); }
        }
      `}</style>
    </div>
  );
}
